import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterModule, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TippyDirective } from '../../directives/tippy.directive';
import TOOLTIP_KEYS from '../../constants/tooltips';
import { Subscription, filter } from 'rxjs';
import { LanguageService } from '../../../core/services/language.service';
import { ThemeService } from '../../../core/services/theme.service';
import { AuthStore } from '../../../store/auth.store';
import { LayoutStore } from '../../../store/layout.store';
import { ViewContextStore } from '../../../store/view-context.store';
import { ChatNotificationStore } from '../../../store/chat-notification.store';
import { PermissionService } from '../../../core/auth/permission.service';
import { environment } from '../../../environments/environment';
import { User } from '../../../core/models/auth.types';

import { BreadcrumbItem } from '../models/private-toolbar.types';
import { maskRouteId } from '../../util/route-id.util';

const HOME_BREADCRUMB_KEY = 'private.breadcrumb.home';

@Component({
  selector: 'app-private-toolbar',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule, TippyDirective],
  templateUrl: './private-toolbar.component.html',
  styleUrl: './private-toolbar.component.scss',
})
export class PrivateToolbarComponent implements OnInit, OnDestroy {
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly authStore = inject(AuthStore);
  private readonly layoutStore = inject(LayoutStore);
  private readonly viewContextStore = inject(ViewContextStore);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly elementRef = inject(ElementRef);
  protected readonly chatNotificationStore = inject(ChatNotificationStore);
  private readonly permissionService = inject(PermissionService);

  languageDropdownOpen = false;
  userDropdownOpen = false;
  notifDropdownOpen = false;
  private lastDropdownToggle = 0;

  readonly breadcrumbs = signal<BreadcrumbItem[]>([]);
  readonly availableLangs = this.languageService.availableLangs;
  readonly currentLang = this.languageService.currentLang;
  readonly isDark = this.themeService.isDark;
  readonly isSidebarExpanded = this.layoutStore.isSidebarExpanded;
  readonly currentUser = this.authStore.currentUser;
  readonly TOOLTIP_KEYS = TOOLTIP_KEYS;

  private routerSub?: Subscription;

  ngOnInit(): void {
    // Ensure private area UI reflects the language stored in the global cookie
    this.languageService.restoreLangFromCookie();
    this.buildBreadcrumbs();
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.buildBreadcrumbs());

    this.chatNotificationStore.startPolling();
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.chatNotificationStore.stopPolling();
  }

  private buildBreadcrumbs(): void {
    const crumbs = this.collectBreadcrumbs();

    if (crumbs.length > 0) crumbs.at(-1)!.isActive = true;

    this.breadcrumbs.set(crumbs);
  }

  private collectBreadcrumbs(): BreadcrumbItem[] {
    // Order and Requester profiles do not have a "Home" page, so hide it from breadcrumbs
    const showHome = !this.permissionService.isOrder() && !this.permissionService.isRequester();
    const crumbs: BreadcrumbItem[] = showHome
      ? [{ labelKey: HOME_BREADCRUMB_KEY, url: '/app', isActive: false }]
      : [];

    let route = this.activatedRoute.root;
    let currentUrl = '';

    while (route.firstChild) {
      route = route.firstChild;
      const snapshot = route.snapshot;
      if (!snapshot.url || snapshot.url.length === 0) continue;

      snapshot.url.forEach(segment => (currentUrl += `/${segment.path}`));

      const breadcrumbKey = snapshot.data['breadcrumb'] as string | undefined; // NOSONAR: runtime data may be untyped
      const isHomeCrumb = breadcrumbKey === HOME_BREADCRUMB_KEY;
      const isDuplicatedHome = isHomeCrumb && crumbs.some(c => c.labelKey === HOME_BREADCRUMB_KEY);
      const isHiddenHomeCrumb = isHomeCrumb && !showHome;

      if (!breadcrumbKey || isDuplicatedHome || isHiddenHomeCrumb) continue;

      const parentBreadcrumb = snapshot.data['parentBreadcrumb'] as string | undefined; // NOSONAR: runtime data may be untyped
      const parentUrl = snapshot.data['parentUrl'] as string | undefined; // NOSONAR: runtime data may be untyped
      const grandparentBreadcrumb = snapshot.data['grandparentBreadcrumb'] as string | undefined; // NOSONAR: runtime data may be untyped
      const grandparentUrl = snapshot.data['grandparentUrl'] as string | undefined; // NOSONAR: runtime data may be untyped

      if (grandparentBreadcrumb) {
        this.pushParentBreadcrumbIfNeeded(crumbs, grandparentBreadcrumb, grandparentUrl, currentUrl);
      }

      if (parentBreadcrumb) {
        this.pushParentBreadcrumbIfNeeded(crumbs, parentBreadcrumb, parentUrl, currentUrl);
      } else if (breadcrumbKey === 'private.breadcrumb.edit_company' || breadcrumbKey === 'private.breadcrumb.new_company') {
        this.pushCompaniesBreadcrumbIfNeeded(crumbs);
      }

      crumbs.push({ labelKey: breadcrumbKey, url: currentUrl, isActive: false });
    }

    return crumbs;
  }

  private pushParentBreadcrumbIfNeeded(
    crumbs: BreadcrumbItem[],
    parentBreadcrumb: string,
    parentUrl: string | undefined,
    currentUrl: string
  ): void {
    const lastCrumb = crumbs.at(-1);
    if (lastCrumb && lastCrumb.labelKey === parentBreadcrumb) return;

    let urlToUse = parentUrl;
    if (!urlToUse && parentBreadcrumb === 'private.breadcrumb.companies') {
      urlToUse = this.getSettingsBaseRoute() + '/companies';
    }

    crumbs.push({ labelKey: parentBreadcrumb, url: urlToUse ?? currentUrl, isActive: false });
  }

  private pushCompaniesBreadcrumbIfNeeded(crumbs: BreadcrumbItem[]): void {
    const companiesCrumbKey = 'private.breadcrumb.companies';
    const companiesUrl = this.getSettingsBaseRoute() + '/companies';
    const lastCrumb = crumbs.at(-1);
    if (lastCrumb && lastCrumb.labelKey === companiesCrumbKey) return;

    crumbs.push({ labelKey: companiesCrumbKey, url: companiesUrl, isActive: false });
  }

  getCurrentLangOption() {
    return this.languageService.getCurrentLangOption();
  }

  toggleLanguageDropdown(event: Event): void {
    event.stopPropagation();
    this.languageDropdownOpen = !this.languageDropdownOpen;
    if (this.languageDropdownOpen) {
      this.userDropdownOpen = false;
      this.notifDropdownOpen = false;
    }
    this.lastDropdownToggle = Date.now();
  }

  changeLanguage(lang: string): void {
    this.languageService.setLang(lang as 'pt' | 'en' | 'es'); // NOSONAR: narrowed to supported Lang union at runtime
    this.languageDropdownOpen = false;
  }

  onLanguageSelectChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null; // NOSONAR: DOM event target narrowing
    const selectedLang = target?.value;
    if (!selectedLang) {
      return;
    }

    this.changeLanguage(selectedLang);
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  toggleUserDropdown(event: Event): void {
    event.stopPropagation();
    this.userDropdownOpen = !this.userDropdownOpen;
    if (this.userDropdownOpen) {
      this.languageDropdownOpen = false;
      this.notifDropdownOpen = false;
    }
    this.lastDropdownToggle = Date.now();
  }

  toggleNotifDropdown(event: Event): void {
    event.stopPropagation();
    this.notifDropdownOpen = !this.notifDropdownOpen;
    if (this.notifDropdownOpen) {
      this.languageDropdownOpen = false;
      this.userDropdownOpen = false;
    }
    this.lastDropdownToggle = Date.now();
  }

  navigateToOfferFromNotif(group: { offerId: string; offerNumber: string; bidIds: string[] }): void {
    this.notifDropdownOpen = false;
    const isCarrier = this.permissionService.isCarrier();
    const base = isCarrier ? '/app/carriers' : '/app/shipper';
    const queryParams: Record<string, string> = { type: 'spot', number: group.offerNumber };
    if (isCarrier) {
      queryParams['tab'] = 'messages';
      if (group.bidIds.length > 0) {
        queryParams['chatBid'] = group.bidIds[0];
      }
    } else if (group.bidIds.length > 0) {
      queryParams['openChatBid'] = group.bidIds[0];
      // Force queryParams change on repeated clicks (same offer/bid) so
      // shipper bids page can reopen sidebar + chat deterministically.
      queryParams['openChatAt'] = String(Date.now());
    }
    this.router.navigate([base, 'offers', 'details', maskRouteId(group.offerId)], { queryParams }).catch(() => null);
  }

  toggleMobileSidebar(): void {
    this.layoutStore.toggleMobileSidebar();
  }

  getUserInitial(): string {
    const user = this.currentUser() ?? this.readUserFromStorage();
    if (user?.name) return String(user.name).charAt(0).toUpperCase();
    return 'U';
  }

  getUserAvatarUrl(): string {
    const user = this.currentUser() ?? this.readUserFromStorage();
    const img = user?.image;
    if (!img) return 'assets/img/svg/abstract-user-flat-grey.svg';
    // If already an absolute URL or includes S3 prefix, return as-is
    if (img.startsWith('http') || img.includes(environment.AWS_S3_ASSETS)) return img;
    return `${environment.AWS_S3_ASSETS}${img}`;
  }

  getUserEmail(): string {
    const user = this.currentUser() ?? this.readUserFromStorage();
    return user?.email ?? 'user@example.com';
  }

  getUserName(): string {
    const user = this.currentUser() ?? this.readUserFromStorage();
    return user?.name ?? 'User';
  }

  /**
   * Fallback read from localStorage for `auth_user` so UI shows avatar/name
   * even if the in-memory store hasn't been hydrated yet.
   */
  private readUserFromStorage(): Partial<User> | null {
    try {
      const raw = localStorage.getItem('auth_user');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<User>; // NOSONAR: parsed JSON typed as Partial<User>
      return parsed ?? null;
    } catch {
      return null;
    }
  }

  goToProfile(): void {
    this.userDropdownOpen = false;
    const settingsBaseRoute = this.getSettingsBaseRoute();
    this.router.navigate([`${settingsBaseRoute}/profile`]);
  }

  logout(): void {
    this.userDropdownOpen = false;
    // ensure auth state is cleared
    this.authStore.logout();
    // restore language from cookie so selects reflect cookie value after logout
    this.languageService.restoreLangFromCookie();
    this.router.navigate(['/login']);
  }

  private getSettingsBaseRoute(): string {
    return this.viewContextStore.activeView() === 'carrier'
      ? '/app/configurations-carrier'
      : '/app/configurations-shipper';
  }

  // On iOS Safari and some Android browsers, touch-synthesized click events can bypass
  // stopPropagation() and reach document-level listeners with unreliable event.target.
  // A timestamp guard prevents duplicate events from closing the dropdown immediately.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (Date.now() - this.lastDropdownToggle < 200) return;

    const target = event.target as Element | null; // NOSONAR: DOM event target narrowing
    if (!target) return;

    if (this.languageDropdownOpen && !target.closest('.lang-wrapper')) {
      this.languageDropdownOpen = false;
    }

    if (this.userDropdownOpen && !target.closest('.user-wrapper')) {
      this.userDropdownOpen = false;
    }

    if (this.notifDropdownOpen && !target.closest('.notif-wrapper')) {
      this.notifDropdownOpen = false;
    }
  }
}
